import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { Mail, Lock, User, UserPlus, Chrome, Loader2 } from "lucide-react";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const LOGO = "https://customer-assets.emergentagent.com/job_ai-legal-test/artifacts/trhkf0y7_justice%20ai%20logo.png";

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ name: "", email: "", password: "", role: "citizen" });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, formData, { withCredentials: true });
      login(response.data.user, response.data.token);
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const roles = [
    { v: "citizen", l: "Citizen seeking legal help" },
    { v: "lawyer", l: "Legal professional" },
    { v: "partner", l: "NGO / Partner organization" },
  ];

  return (
    <main className="flex-1 relative min-h-[calc(100vh-5rem)] bg-ink flex items-center justify-center py-16 px-4 overflow-hidden" data-testid="register-page">
      <div className="absolute inset-0 opacity-50"><NeuralNetworkBackground density={20} /></div>
      <ScaleWatermark />
      <div className="turquoise-aura -top-32 right-0 opacity-50" />
      <div className="gold-aura -bottom-40 -left-40 opacity-40" />
      <div className="grain-overlay" />

      <div className="relative z-10 w-full max-w-md glass-strong rounded-3xl p-8 sm:p-10" data-testid="register-card">
        <div className="text-center mb-7">
          <img src={LOGO} alt="AccessJustice" className="h-20 w-auto mx-auto mb-5" />
          <div className="eyebrow mb-2">Create Account</div>
          <h1 className="font-playfair text-3xl text-white">
            Join <span className="italic text-aj-gradient">AccessJustice.Global</span>
          </h1>
          <p className="font-inter text-sm text-white/55 mt-2">Start your journey to accessible justice.</p>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          data-testid="google-register-btn"
          className="w-full flex items-center justify-center gap-3 px-5 py-3 rounded-full border border-white/15 hover:border-turquoise/40 bg-white/[0.02] hover:bg-white/[0.05] text-white font-montserrat text-xs uppercase tracking-[0.16em] font-semibold transition-all"
        >
          <Chrome className="w-4 h-4" /> Continue with Google
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
          <div className="relative flex justify-center"><span className="bg-ink px-3 font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/40">or with email</span></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Full Name</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your full name"
                required
                data-testid="name-input"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 focus:bg-white/[0.06] transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                required
                data-testid="email-input"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 focus:bg-white/[0.06] transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="password-input"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 focus:bg-white/[0.06] transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">I am a</label>
            <div className="grid grid-cols-3 gap-2" data-testid="role-select">
              {roles.map((r) => (
                <button
                  key={r.v}
                  type="button"
                  onClick={() => setFormData({ ...formData, role: r.v })}
                  data-testid={`role-${r.v}`}
                  className={`px-3 py-2.5 rounded-full font-montserrat text-[10px] uppercase tracking-wider font-semibold transition-all ${
                    formData.role === r.v
                      ? "bg-turquoise text-ink"
                      : "bg-white/[0.03] border border-white/10 text-white/70 hover:border-turquoise/40"
                  }`}
                >
                  {r.l.split(" ")[0]}
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            data-testid="register-submit-btn"
            className="btn-aj-primary w-full !py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Creating Account…</>) : (<><UserPlus className="w-4 h-4" /> Create Account</>)}
          </button>
        </form>

        <p className="text-center font-inter text-sm text-white/55 mt-6">
          Already have an account?{" "}
          <Link to="/login" data-testid="login-link" className="text-turquoise hover:text-turquoise-200 font-semibold transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
};

export default Register;
